/**
 * Protest Resolution Screen — Both teams vote on disputed cards.
 */

import { getState, voteProtest, hostResolveProtest, skipProtests } from '../game-state';
import { navigate } from '../router';
import { t } from '../i18n';

export function renderProtest(): void {
  const app = document.getElementById('app')!;
  const state = getState();
  const summary = state.lastTurnSummary;

  if (!summary || summary.protests.length === 0) {
    skipProtests();
    navigate('turn-waiting');
    return;
  }

  const protests = summary.protests;

  app.innerHTML = `
    <div class="stack">
      <h1 style="font-size: var(--font-size-xl);">${t('protest')}</h1>
      <p class="subtitle">${t('protestSubtitle')}</p>

      ${protests.map((protest, i) => {
        if (protest.resolved) {
          return `
            <div class="protest-card" style="opacity: 0.6;">
              <div class="protest-title">"${protest.cardTitle}"</div>
              <p class="text-secondary" style="font-size: var(--font-size-sm);">
                ${protest.result === 'upheld' ? `✗ ${t('protestUpheld')}` : `✓ ${t('protestDismissed')}`}
              </p>
            </div>
          `;
        }

        const needsHostTiebreak = protest.teamAVote && protest.teamBVote && !protest.resolved;

        return `
          <div class="protest-card">
            <div class="protest-title">"${protest.cardTitle}" (${protest.cardPoints} ${protest.cardPoints > 1 ? t('pts') : t('pt')})</div>

            ${needsHostTiebreak ? `
              <p class="text-secondary" style="font-size: var(--font-size-sm); margin: var(--space-sm) 0;">
                ${t('teamsDisagreeHostDecides')}
              </p>
              <div class="vote-buttons">
                <button class="btn btn-primary" data-host-resolve="${i}" data-decision="dismissed">${t('valid')}</button>
                <button class="btn btn-danger" data-host-resolve="${i}" data-decision="upheld">${t('invalid')}</button>
              </div>
            ` : `
              <!-- Team A Vote -->
              <div style="margin-top: var(--space-md);">
                <p class="setting-label">${t('teamASays')}</p>
                <div class="vote-buttons">
                  <button class="btn ${protest.teamAVote === 'valid' ? 'btn-primary' : 'btn-secondary'}"
                    data-vote-team="A" data-vote-index="${i}" data-vote="valid"
                    ${protest.teamAVote ? 'disabled' : ''}>${t('valid')}</button>
                  <button class="btn ${protest.teamAVote === 'invalid' ? 'btn-danger' : 'btn-secondary'}"
                    data-vote-team="A" data-vote-index="${i}" data-vote="invalid"
                    ${protest.teamAVote ? 'disabled' : ''}>${t('invalid')}</button>
                </div>
              </div>

              <!-- Team B Vote -->
              <div style="margin-top: var(--space-sm);">
                <p class="setting-label">${t('teamBSays')}</p>
                <div class="vote-buttons">
                  <button class="btn ${protest.teamBVote === 'valid' ? 'btn-primary' : 'btn-secondary'}"
                    data-vote-team="B" data-vote-index="${i}" data-vote="valid"
                    ${protest.teamBVote ? 'disabled' : ''}>${t('valid')}</button>
                  <button class="btn ${protest.teamBVote === 'invalid' ? 'btn-danger' : 'btn-secondary'}"
                    data-vote-team="B" data-vote-index="${i}" data-vote="invalid"
                    ${protest.teamBVote ? 'disabled' : ''}>${t('invalid')}</button>
                </div>
              </div>
            `}
          </div>
        `;
      }).join('')}

      <button class="btn btn-tertiary" id="btn-skip-protests">${t('skipAllContinue')}</button>
    </div>
  `;

  // Vote handlers
  document.querySelectorAll('[data-vote-team]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const el = btn as HTMLElement;
      const team = el.dataset.voteTeam as 'A' | 'B';
      const index = parseInt(el.dataset.voteIndex!);
      const vote = el.dataset.vote as 'valid' | 'invalid';
      voteProtest(index, team, vote);
      renderProtest(); // re-render
    });
  });

  // Host tiebreak handlers
  document.querySelectorAll('[data-host-resolve]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const el = btn as HTMLElement;
      const index = parseInt(el.dataset.hostResolve!);
      const decision = el.dataset.decision as 'upheld' | 'dismissed';
      hostResolveProtest(index, decision);
      renderProtest(); // re-render
    });
  });

  // Skip all
  document.getElementById('btn-skip-protests')?.addEventListener('click', () => {
    skipProtests();
    const s = getState();
    if (s.phase === 'round-complete') {
      navigate('round-complete');
    } else {
      navigate('turn-waiting');
    }
  });

  // Auto-advance if all protests resolved
  const allResolved = protests.every((p) => p.resolved);
  if (allResolved) {
    // Add a "Done" button
    const doneBtn = document.createElement('button');
    doneBtn.className = 'btn btn-primary';
    doneBtn.textContent = t('doneContinue');
    doneBtn.style.marginTop = 'var(--space-md)';
    doneBtn.addEventListener('click', () => {
      skipProtests();
      const s = getState();
      if (s.phase === 'round-complete') {
        navigate('round-complete');
      } else {
        navigate('turn-waiting');
      }
    });
    app.querySelector('.stack')?.appendChild(doneBtn);
  }
}
